import * as React from 'react'
import { useQuery } from '@tanstack/react-query'
import { Alert } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { ApiError, getJSON, patchJSON } from '@/lib/api'
import type { CustomFieldsResponse } from '@/lib/contacts/types'

interface BulkFieldSkipped {
  contact_id: number
  contact_name: string
  reason: string
}

interface BulkFieldUpdateResponse {
  bulk_field_update: {
    selected_count: number
    overwrite_count: number
    changed_count: number
    skipped: BulkFieldSkipped[]
  }
}

interface BulkFieldUpdatePayload {
  field: BulkFieldDescriptor
  value: string
  contact_ids: number[]
  confirm: boolean
}

type BulkFieldDescriptor =
  | { kind: 'standard'; name: 'email' | 'phone' }
  | { kind: 'custom_field'; custom_field_id: number }

interface BulkFieldOption {
  key: string
  label: string
  field: BulkFieldDescriptor
}

const STANDARD_BULK_FIELD_OPTIONS: BulkFieldOption[] = [
  { key: 'standard:email', label: 'Email', field: { kind: 'standard', name: 'email' } },
  { key: 'standard:phone', label: 'Phone', field: { kind: 'standard', name: 'phone' } },
]

export function ContactsBulkSetFieldDialog({
  open,
  onOpenChange,
  contactIds,
  onApplied,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  contactIds: number[]
  onApplied: () => void
}) {
  const [fieldKey, setFieldKey] = React.useState(STANDARD_BULK_FIELD_OPTIONS[0].key)
  const [value, setValue] = React.useState('')
  const [preview, setPreview] = React.useState<BulkFieldUpdateResponse['bulk_field_update'] | null>(
    null,
  )
  const [error, setError] = React.useState<string | null>(null)
  const [isSubmitting, setIsSubmitting] = React.useState(false)
  const customFieldsQuery = useQuery({
    queryKey: ['custom-fields', 'bulk-set'],
    queryFn: () => getJSON<CustomFieldsResponse>('/api/v1/custom_fields'),
    enabled: open,
    retry: false,
  })
  const options = React.useMemo(
    () => [
      ...STANDARD_BULK_FIELD_OPTIONS,
      ...((customFieldsQuery.data?.custom_fields ?? [])
        .filter((field) => field.active)
        .map((field) => ({
          key: `custom:${field.id}`,
          label: field.label,
          field: { kind: 'custom_field' as const, custom_field_id: field.id },
        })) satisfies BulkFieldOption[]),
    ],
    [customFieldsQuery.data?.custom_fields],
  )
  const selectedField = options.find((option) => option.key === fieldKey) ?? options[0]

  React.useEffect(() => {
    if (!open) return

    setFieldKey(STANDARD_BULK_FIELD_OPTIONS[0].key)
    setValue('')
    setPreview(null)
    setError(null)
  }, [open])

  function resetPreview() {
    setPreview(null)
    setError(null)
  }

  async function previewChanges() {
    if (!selectedField) return

    setIsSubmitting(true)
    setError(null)
    try {
      const response = await previewBulkFieldUpdate(
        bulkFieldPayload(selectedField.field, value, contactIds, false),
      )
      setPreview(response.bulk_field_update)
    } catch (mutationError) {
      setPreview(null)
      setError(mutationError instanceof Error ? mutationError.message : 'Bulk field update failed.')
    } finally {
      setIsSubmitting(false)
    }
  }

  async function applyChanges() {
    if (!selectedField) return

    setIsSubmitting(true)
    setError(null)
    try {
      await patchJSON<BulkFieldUpdateResponse>('/api/v1/contacts/bulk_field_update', {
        bulk_field_update: bulkFieldPayload(selectedField.field, value, contactIds, true),
      })
      onApplied()
      onOpenChange(false)
    } catch (mutationError) {
      setError(mutationError instanceof Error ? mutationError.message : 'Bulk field update failed.')
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Set a field on {contactIds.length} contacts</DialogTitle>
        </DialogHeader>
        <div className="flex flex-col gap-4">
          {error && <Alert>{error}</Alert>}
          <BulkFieldSelect
            options={options}
            selectedKey={selectedField?.key}
            onChange={(nextFieldKey) => {
              setFieldKey(nextFieldKey)
              resetPreview()
            }}
          />
          <BulkValueInput
            value={value}
            onChange={(nextValue) => {
              setValue(nextValue)
              resetPreview()
            }}
          />
          {preview && <BulkFieldPreview preview={preview} />}
        </div>
        <BulkFieldDialogFooter
          preview={preview}
          disabled={isSubmitting || value.trim().length === 0 || customFieldsQuery.isLoading}
          onCancel={() => onOpenChange(false)}
          onSubmit={preview ? applyChanges : previewChanges}
        />
      </DialogContent>
    </Dialog>
  )
}

function BulkFieldSelect({
  options,
  selectedKey,
  onChange,
}: {
  options: BulkFieldOption[]
  selectedKey: string | undefined
  onChange: (key: string) => void
}) {
  return (
    <div className="flex flex-col gap-1.5">
      <Label htmlFor="bulk-field">Field</Label>
      <Select value={selectedKey} onValueChange={onChange}>
        <SelectTrigger id="bulk-field">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {options.map((option) => (
            <SelectItem key={option.key} value={option.key}>
              {option.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  )
}

function BulkValueInput({ value, onChange }: { value: string; onChange: (value: string) => void }) {
  return (
    <div className="flex flex-col gap-1.5">
      <Label htmlFor="bulk-value">Value</Label>
      <Input id="bulk-value" value={value} onChange={(event) => onChange(event.target.value)} />
    </div>
  )
}

function BulkFieldPreview({ preview }: { preview: BulkFieldUpdateResponse['bulk_field_update'] }) {
  return (
    <div className="rounded-md border border-border bg-accent/40 px-3 py-2 text-body-sm text-muted-foreground">
      {preview.selected_count} selected, {preview.overwrite_count}{' '}
      {preview.overwrite_count === 1 ? 'overwrite' : 'overwrites'}.
    </div>
  )
}

function BulkFieldDialogFooter({
  preview,
  disabled,
  onCancel,
  onSubmit,
}: {
  preview: BulkFieldUpdateResponse['bulk_field_update'] | null
  disabled: boolean
  onCancel: () => void
  onSubmit: () => void
}) {
  return (
    <DialogFooter>
      <Button type="button" variant="outline" onClick={onCancel}>
        Cancel
      </Button>
      <Button type="button" disabled={disabled} onClick={onSubmit}>
        {preview ? `Apply to ${preview.selected_count}` : 'Preview changes'}
      </Button>
    </DialogFooter>
  )
}

async function previewBulkFieldUpdate(
  bulkFieldUpdate: BulkFieldUpdatePayload,
): Promise<BulkFieldUpdateResponse> {
  try {
    return await patchJSON<BulkFieldUpdateResponse>('/api/v1/contacts/bulk_field_update', {
      bulk_field_update: bulkFieldUpdate,
    })
  } catch (error) {
    if (error instanceof ApiError && error.status === 409) {
      return error.body as BulkFieldUpdateResponse
    }
    throw error
  }
}

function bulkFieldPayload(
  field: BulkFieldDescriptor,
  value: string,
  contactIds: number[],
  confirmed: boolean,
): BulkFieldUpdatePayload {
  return {
    field,
    value,
    contact_ids: contactIds,
    confirm: confirmed,
  }
}
